import React, { useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { resetPassword } from "../../services/auth";

export default function ResetPasswordForm({ userId, onSuccess, onCancel }) {
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleReset = async () => {
    setError("");

    if (!userId) return setError("معرف المستخدم غير متوفر.");
    if (!newPassword || !newPasswordConfirmation)
      return setError("يرجى ملء الحقول المطلوبة.");
    if (newPassword !== newPasswordConfirmation)
      return setError("كلمتا المرور غير متطابقتين.");

    setLoading(true);
    try {
      const res = await resetPassword({
        user_id: userId,
        new_password: newPassword,
        new_password_confirmation: newPasswordConfirmation,
      });

      const msg = res?.message || "تم إعادة تعيين كلمة المرور بنجاح";
      if (typeof onSuccess === "function") onSuccess(msg);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "فشل إعادة التعيين.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "grey.100",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        animation: "fadeInScale 0.5s ease-out",
        "@keyframes fadeInScale": {
          from: { opacity: 0, transform: "scale(0.95)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: "auto",
            mb: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 24px rgba(213, 170, 47, 0.3)",
          }}
        >
          <LockResetIcon sx={{ fontSize: 32, color: "white" }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            mb: 0.5,
            background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          إعادة تعيين كلمة المرور
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          أدخل كلمة المرور الجديدة
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 3,
            animation: "shake 0.5s ease-in-out",
            "@keyframes shake": {
              "0%, 100%": { transform: "translateX(0)" },
              "25%": { transform: "translateX(-10px)" },
              "75%": { transform: "translateX(10px)" },
            },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Form */}
      <Stack spacing={2.5}>
        <TextField
          label="كلمة المرور الجديدة"
          type={showPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: "primary.main" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="تأكيد كلمة المرور الجديدة"
          type={showConfirmPassword ? "text" : "password"}
          value={newPasswordConfirmation}
          onChange={(e) => setNewPasswordConfirmation(e.target.value)}
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CheckCircleIcon sx={{ color: "primary.main" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Password Match Indicator */}
        {newPassword && newPasswordConfirmation && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderRadius: 2,
              bgcolor: newPassword === newPasswordConfirmation ? "success.50" : "error.50",
              border: "1px solid",
              borderColor: newPassword === newPasswordConfirmation ? "success.main" : "error.main",
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 20,
                color: newPassword === newPasswordConfirmation ? "success.main" : "error.main",
              }}
            />
            <Typography
              variant="caption"
              fontWeight={700}
              color={newPassword === newPasswordConfirmation ? "success.dark" : "error.dark"}
            >
              {newPassword === newPasswordConfirmation
                ? "كلمتا المرور متطابقتان ✓"
                : "كلمتا المرور غير متطابقتين"}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleReset}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LockResetIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 800,
              borderRadius: 3,
              textTransform: "none",
              background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
              boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
              },
              "&:disabled": {
                background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
              },
            }}
          >
            {loading ? "جاري التحديث..." : "إعادة تعيين كلمة المرور"}
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              setNewPassword("");
              setNewPasswordConfirmation("");
              if (typeof onCancel === "function") onCancel();
            }}
            sx={{
              px: 3,
              py: 1.5,
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
                bgcolor: "grey.50",
              },
            }}
          >
            إلغاء
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
